import {
  APPLY_DISCOUNT_CODE_TO_ORG_MUTATION,
  ApplyDiscountCodeInput,
  ApplyDiscountCodeResponse,
  BillingInterval,
  BillingModel,
  CANCEL_DOWNGRADE_ORG_PLAN_MUTATION,
  CANCEL_ORG_SUBSCRIPTION_MUTATION,
  CREATE_DISCOUNT_CODE_MUTATION,
  CREATE_INVOICE_PREVIEW,
  CREATE_STRIPE_CUSTOMER_MUTATION,
  CreateDiscountCodeInput,
  CreateStripeCustomerResponse,
  CreateSubscriptionStatus,
  CustomRateReference,
  DELETE_CUSTOM_PRICE_MUTATION,
  DELETE_DISCOUNT_CODE_MUTATION,
  DiscountCode,
  DOWNGRADE_PLAN_MUTATION,
  GET_INVOICES,
  GET_ORG_CUSTOM_PRICES,
  GET_ORGANIZATION_BILLING,
  GET_PAYMENT_INTENT,
  Invoice,
  LIST_DISCOUNT_CODES_QUERY,
  MANUAL_PAY_INVOICE_MUTATION,
  MARK_INVOICE_AS_PAID_MUTATION,
  MarkInvoiceAsPaidResponse,
  OrganizationBilling,
  PaymentIntent,
  REACTIVATE_ORG_SUBSCRIPTION_MUTATION,
  REMOVE_DISCOUNT_CODE_FROM_ORG_MUTATION,
  RemoveDiscountCodeInput,
  RemoveDiscountCodeResponse,
  RESTORE_ORG_SUBSCRIPTION_MUTATION,
  SAVE_CUSTOM_PRICES_MUTATION,
  SAVE_ORGANIZATION_BILLING_DETAILS_MUTATION,
  SaveCustomPriceInput,
  SaveCustomPriceResult,
  UpdateOrganizationBillingInput,
  UPGRADE_PLAN_MUTATION,
} from '@graphql';
import { graphqlApi } from '@utils';
import { flow, makeAutoObservable } from 'mobx';
import { toast } from 'react-toastify';
import { RequestState, RequestStatus, handleError, handleMutationResponse } from './defaults';

export class BillingStore {
  invoicesData: RequestState<Invoice[]> = {
    data: null,
    status: RequestStatus.LOADING,
    error: null,
  };

  organizationBillingData: RequestState<OrganizationBilling> = {
    data: null,
    status: RequestStatus.LOADING,
    error: null,
  };

  cancelSubscriptionData: RequestState<void> = {
    data: null,
    status: RequestStatus.LOADING,
    error: null,
  };

  restoreSubscriptionData: RequestState<void> = {
    data: null,
    status: RequestStatus.LOADING,
    error: null,
  };

  manualPayInvoiceData: RequestState<void> = {
    data: null,
    status: RequestStatus.LOADING,
    error: null,
  };

  markInvoiceAsPaidData: RequestState<MarkInvoiceAsPaidResponse> = {
    data: null,
    status: RequestStatus.SUCCESS,
    error: null,
  };

  paymentIntentData: RequestState<PaymentIntent> = {
    data: null,
    status: RequestStatus.LOADING,
    error: null,
  };

  reactivateSubscriptionData: RequestState<void> = {
    data: null,
    status: RequestStatus.LOADING,
    error: null,
  };

  createInvoicePreviewData: RequestState<void> = {
    data: null,
    status: RequestStatus.SUCCESS,
    error: null,
  };

  discountCodesData: RequestState<DiscountCode[]> = {
    data: null,
    status: RequestStatus.LOADING,
    error: null,
  };

  createDiscountCodeData: RequestState<DiscountCode> = {
    data: null,
    status: RequestStatus.SUCCESS,
    error: null,
  };

  deleteDiscountCodeData: RequestState<void> = {
    data: null,
    status: RequestStatus.SUCCESS,
    error: null,
  };

  applyDiscountCodeData: RequestState<ApplyDiscountCodeResponse> = {
    data: null,
    status: RequestStatus.SUCCESS,
    error: null,
  };

  removeDiscountCodeData: RequestState<RemoveDiscountCodeResponse> = {
    data: null,
    status: RequestStatus.SUCCESS,
    error: null,
  };

  upgradeOrganizationPlanData: RequestState<void> = {
    data: null,
    status: RequestStatus.LOADING,
    error: null,
  };

  downgradeOrganizationPlanData: RequestState<void> = {
    data: null,
    status: RequestStatus.LOADING,
    error: null,
  };

  cancelDowngradeOrganizationPlanData: RequestState<void> = {
    data: null,
    status: RequestStatus.LOADING,
    error: null,
  };

  saveCustomPricesData: RequestState<SaveCustomPriceResult[]> = {
    data: null,
    status: RequestStatus.LOADING,
    error: null,
  };

  customRatesData: RequestState<CustomRateReference[]> = {
    data: null,
    status: RequestStatus.LOADING,
    error: null,
  };

  removeCustomPriceData: RequestState<void> = {
    data: null,
    status: RequestStatus.SUCCESS,
    error: null,
  };

  saveOrganizationBillingDetailsData: RequestState<{
    success: boolean;
    flowType: string;
    message: string;
  }> = {
    data: null,
    status: RequestStatus.LOADING,
    error: null,
  };

  createStripeCustomerData: RequestState<CreateStripeCustomerResponse> = {
    data: null,
    status: RequestStatus.LOADING,
    error: null,
  };

  constructor() {
    makeAutoObservable(this);
  }

  getInvoices = flow(function* getInvoices(this: BillingStore, orgId: string) {
    try {
      this.invoicesData.status = RequestStatus.LOADING;
      const getInvoicesResponse = yield graphqlApi(GET_INVOICES, { orgId });
      this.invoicesData.data = getInvoicesResponse?.data?.data?.getInvoicesByOrgId.invoices;
      this.invoicesData.status = RequestStatus.SUCCESS;
    } catch (err) {
      toast('Something went wrong!', { type: 'error' });
      this.invoicesData.data = null;
      this.invoicesData.status = RequestStatus.FAILED;
      this.invoicesData.error = handleError(err);
    }
  }).bind(this);

  getOrganizationBilling = flow(function* getOrganizationBilling(
    this: BillingStore,
    orgId: string,
  ) {
    try {
      this.organizationBillingData.status = RequestStatus.LOADING;
      const getOrganizationBillingResponse = yield graphqlApi(GET_ORGANIZATION_BILLING, { orgId });
      this.organizationBillingData.data =
        getOrganizationBillingResponse?.data?.data?.getOrganizationBillingByOrgId;
      this.organizationBillingData.status = RequestStatus.SUCCESS;
    } catch (err) {
      toast('Something went wrong!', { type: 'error' });
      this.organizationBillingData.data = null;
      this.organizationBillingData.status = RequestStatus.FAILED;
      this.organizationBillingData.error = handleError(err);
    }
  }).bind(this);

  getPaymentIntent = flow(function* getPaymentIntent(this: BillingStore, paymentIntentId: string) {
    try {
      this.paymentIntentData.status = RequestStatus.LOADING;
      const getPaymentIntentResponse = yield graphqlApi(GET_PAYMENT_INTENT, { paymentIntentId });
      this.paymentIntentData.data = getPaymentIntentResponse?.data?.data?.getPaymentIntent;
      this.paymentIntentData.status = RequestStatus.SUCCESS;
    } catch (err) {
      toast('Something went wrong!', { type: 'error' });
      this.paymentIntentData.data = null;
      this.paymentIntentData.status = RequestStatus.FAILED;
      this.paymentIntentData.error = handleError(err);
    }
  }).bind(this);

  cancelSubscription = flow(function* cancelSubscription(this: BillingStore, orgId: string) {
    try {
      this.cancelSubscriptionData.status = RequestStatus.LOADING;
      const cancelSubscriptionResponse = yield graphqlApi(CANCEL_ORG_SUBSCRIPTION_MUTATION, {
        orgId,
      });

      const data = handleMutationResponse(cancelSubscriptionResponse, 'cancelOrgSubscription');

      this.cancelSubscriptionData.data = data;
      this.cancelSubscriptionData.status = RequestStatus.SUCCESS;
      return data;
    } catch (err) {
      toast('Something went wrong!', { type: 'error' });
      this.cancelSubscriptionData.data = null;
      this.cancelSubscriptionData.status = RequestStatus.FAILED;
      this.cancelSubscriptionData.error = handleError(err);
      return null;
    }
  }).bind(this);

  restoreSubscription = flow(function* restoreSubscription(this: BillingStore, orgId: string) {
    try {
      this.restoreSubscriptionData.status = RequestStatus.LOADING;
      const restoreSubscriptionResponse = yield graphqlApi(RESTORE_ORG_SUBSCRIPTION_MUTATION, {
        orgId,
      });

      const data = handleMutationResponse(restoreSubscriptionResponse, 'restoreOrgSubscription');

      this.restoreSubscriptionData.data = data;
      this.restoreSubscriptionData.status = RequestStatus.SUCCESS;
      return data;
    } catch (err) {
      toast('Something went wrong!', { type: 'error' });
      this.restoreSubscriptionData.data = null;
      this.restoreSubscriptionData.status = RequestStatus.FAILED;
      this.restoreSubscriptionData.error = handleError(err);
      return null;
    }
  }).bind(this);

  manualPayInvoice = flow(function* manualPayInvoice(
    this: BillingStore,
    invoiceId: string,
    orgId: string,
  ) {
    try {
      this.manualPayInvoiceData.status = RequestStatus.LOADING;
      const manualPayInvoiceResponse = yield graphqlApi(MANUAL_PAY_INVOICE_MUTATION, {
        input: { invoiceId, orgId },
      });

      const data = handleMutationResponse(manualPayInvoiceResponse, 'manualPayInvoice');

      this.manualPayInvoiceData.data = data;
      this.manualPayInvoiceData.status = RequestStatus.SUCCESS;
      return data;
    } catch (err) {
      toast('Something went wrong!', { type: 'error' });
      this.manualPayInvoiceData.data = null;
      this.manualPayInvoiceData.status = RequestStatus.FAILED;
      this.manualPayInvoiceData.error = handleError(err);
      return null;
    }
  }).bind(this);

  markInvoiceAsPaid = flow(function* markInvoiceAsPaid(this: BillingStore, invoiceId: string, orgId: string) {
    try {
      this.markInvoiceAsPaidData.status = RequestStatus.LOADING;
      const markInvoiceAsPaidResponse = yield graphqlApi(MARK_INVOICE_AS_PAID_MUTATION, {
        input: { invoiceId, orgId },
      });

      const data = handleMutationResponse(markInvoiceAsPaidResponse, 'markInvoiceAsPaid');

      this.markInvoiceAsPaidData.data = data;
      this.markInvoiceAsPaidData.status = RequestStatus.SUCCESS;
      return data;
    } catch (err) {
      toast('Something went wrong!', { type: 'error' });
      this.markInvoiceAsPaidData.data = null;
      this.markInvoiceAsPaidData.status = RequestStatus.FAILED;
      this.markInvoiceAsPaidData.error = handleError(err);
      return null;
    }
  }).bind(this);

  reactivateSubscription = flow(function* reactivateSubscription(
    this: BillingStore,
    orgId: string,
  ) {
    try {
      this.reactivateSubscriptionData.status = RequestStatus.LOADING;
      const reactivateSubscriptionResponse = yield graphqlApi(
        REACTIVATE_ORG_SUBSCRIPTION_MUTATION,
        { orgId },
      );

      const data = handleMutationResponse(
        reactivateSubscriptionResponse,
        'reactivateOrgSubscription',
      );

      this.reactivateSubscriptionData.data = data;

      if (data?.success) {
        this.reactivateSubscriptionData.status = RequestStatus.SUCCESS;
      } else {
        this.reactivateSubscriptionData.status = RequestStatus.FAILED;
        this.reactivateSubscriptionData.error =
          data?.subscriptionStatus === CreateSubscriptionStatus.incomplete
            ? 'Subscription is incomplete. Please update your payment method and try again.'
            : 'Something went wrong!';
      }
      return data;
    } catch (err) {
      toast('Something went wrong!', { type: 'error' });
      this.reactivateSubscriptionData.data = null;
      this.reactivateSubscriptionData.status = RequestStatus.FAILED;
      this.reactivateSubscriptionData.error = handleError(err);
      return null;
    }
  }).bind(this);

  createInvoicePreview = flow(function* createInvoicePreview(this: BillingStore, orgId: string) {
    try {
      this.createInvoicePreviewData.status = RequestStatus.LOADING;
      const createInvoicePreviewResponse = yield graphqlApi(CREATE_INVOICE_PREVIEW, { orgId });
      const data = handleMutationResponse(createInvoicePreviewResponse, 'createInvoicePreview');
      this.createInvoicePreviewData.data = data;
      this.createInvoicePreviewData.status = RequestStatus.SUCCESS;
      return data;
    } catch (err) {
      toast('Something went wrong!', { type: 'error' });
      this.createInvoicePreviewData.data = null;
      this.createInvoicePreviewData.status = RequestStatus.FAILED;
      this.createInvoicePreviewData.error = handleError(err);
      return null;
    }
  }).bind(this);

  listDiscountCodes = flow(function* listDiscountCodes(this: BillingStore) {
    try {
      this.discountCodesData.status = RequestStatus.LOADING;
      const listDiscountCodesResponse = yield graphqlApi(LIST_DISCOUNT_CODES_QUERY);
      this.discountCodesData.data =
        listDiscountCodesResponse?.data?.data?.listDiscountCodes?.discountCodes || [];
      this.discountCodesData.status = RequestStatus.SUCCESS;
      return this.discountCodesData.data;
    } catch (err) {
      toast('Failed to load discount codes', { type: 'error' });
      this.discountCodesData.data = [];
      this.discountCodesData.status = RequestStatus.FAILED;
      this.discountCodesData.error = handleError(err);
      return [];
    }
  }).bind(this);

  createDiscountCode = flow(function* createDiscountCode(
    this: BillingStore,
    input: CreateDiscountCodeInput,
  ) {
    try {
      this.createDiscountCodeData.status = RequestStatus.LOADING;
      // TODO: Check if we want to keep this
      const inputWithMaxRedemptions = {
        ...input,
        maxRedemptions: input.maxRedemptions || 1,
      };
      const createDiscountCodeResponse = yield graphqlApi(CREATE_DISCOUNT_CODE_MUTATION, {
        input: inputWithMaxRedemptions,
      });

      const data = handleMutationResponse(createDiscountCodeResponse, 'createDiscountCode');

      if (data?.success && data?.discountCode) {
        this.createDiscountCodeData.data = data.discountCode;
        this.createDiscountCodeData.status = RequestStatus.SUCCESS;

        // Add the new discount code to the list
        if (this.discountCodesData.data) {
          this.discountCodesData.data = [data.discountCode, ...this.discountCodesData.data];
        }

        toast('Discount code created successfully!', { type: 'success' });
        return data.discountCode;
      }

      toast('Failed to create discount code', { type: 'error' });
      this.createDiscountCodeData.status = RequestStatus.FAILED;
      return null;
    } catch (err) {
      toast('Failed to create discount code', { type: 'error' });
      this.createDiscountCodeData.data = null;
      this.createDiscountCodeData.status = RequestStatus.FAILED;
      this.createDiscountCodeData.error = handleError(err);
      return null;
    }
  }).bind(this);

  deleteDiscountCode = flow(function* deleteDiscountCode(
    this: BillingStore,
    promotionCodeId: string,
  ) {
    try {
      this.deleteDiscountCodeData.status = RequestStatus.LOADING;
      const deleteDiscountCodeResponse = yield graphqlApi(DELETE_DISCOUNT_CODE_MUTATION, {
        promotionCodeId,
      });

      const data = handleMutationResponse(deleteDiscountCodeResponse, 'deleteDiscountCode');

      if (data?.success) {
        this.deleteDiscountCodeData.status = RequestStatus.SUCCESS;

        // Remove the deleted discount code from the list
        if (this.discountCodesData.data) {
          this.discountCodesData.data = this.discountCodesData.data.filter(
            (code) => code.id !== promotionCodeId,
          );
        }

        toast('Discount code deleted successfully!', { type: 'success' });
        return true;
      }

      toast('Failed to delete discount code', { type: 'error' });
      this.deleteDiscountCodeData.status = RequestStatus.FAILED;
      return false;
    } catch (err) {
      toast('Failed to delete discount code', { type: 'error' });
      this.deleteDiscountCodeData.status = RequestStatus.FAILED;
      this.deleteDiscountCodeData.error = handleError(err);
      return false;
    }
  }).bind(this);

  applyDiscountCodeToOrg = flow(function* applyDiscountCodeToOrg(
    this: BillingStore,
    input: ApplyDiscountCodeInput,
  ) {
    try {
      // Prevent applying discount codes for Invoice billing organizations
      // This is a defensive check - UI should already prevent this
      if (this.organizationBilling?.billingModel === BillingModel.INVOICE) {
        toast('Discount codes are not supported for Invoice payment method', { type: 'error' });
        this.applyDiscountCodeData.status = RequestStatus.FAILED;
        this.applyDiscountCodeData.error = 'Discount codes are not supported for Invoice payment method';
        return null;
      }

      this.applyDiscountCodeData.status = RequestStatus.LOADING;
      const applyDiscountCodeResponse = yield graphqlApi(APPLY_DISCOUNT_CODE_TO_ORG_MUTATION, {
        input,
      });

      const data = handleMutationResponse(
        applyDiscountCodeResponse,
        'applyDiscountCodeToOrganization',
      );

      if (data?.success) {
        this.applyDiscountCodeData.data = data;
        this.applyDiscountCodeData.status = RequestStatus.SUCCESS;
        toast('Discount code applied successfully!', { type: 'success' });

        // Refetch billing data
        yield this.getOrganizationBilling(input.orgId);

        return data;
      }

      toast(data?.message || 'Failed to apply discount code', { type: 'error' });
      this.applyDiscountCodeData.status = RequestStatus.FAILED;
      return null;
    } catch (err) {
      toast('Failed to apply discount code', { type: 'error' });
      this.applyDiscountCodeData.data = null;
      this.applyDiscountCodeData.status = RequestStatus.FAILED;
      this.applyDiscountCodeData.error = handleError(err);
      return null;
    }
  }).bind(this);

  removeDiscountCodeFromOrg = flow(function* removeDiscountCodeFromOrg(
    this: BillingStore,
    input: RemoveDiscountCodeInput,
  ) {
    try {
      this.removeDiscountCodeData.status = RequestStatus.LOADING;
      const removeDiscountCodeResponse = yield graphqlApi(REMOVE_DISCOUNT_CODE_FROM_ORG_MUTATION, {
        input,
      });

      const data = handleMutationResponse(
        removeDiscountCodeResponse,
        'removeDiscountCodeFromOrganization',
      );

      if (data?.success) {
        this.removeDiscountCodeData.data = data;
        this.removeDiscountCodeData.status = RequestStatus.SUCCESS;
        toast('Discount code removed successfully!', { type: 'success' });

        // Refetch billing data
        yield this.getOrganizationBilling(input.orgId);

        return data;
      }

      toast(data?.message || 'Failed to remove discount code', { type: 'error' });
      this.removeDiscountCodeData.status = RequestStatus.FAILED;
      return null;
    } catch (err) {
      toast('Failed to remove discount code', { type: 'error' });
      this.removeDiscountCodeData.data = null;
      this.removeDiscountCodeData.status = RequestStatus.FAILED;
      this.removeDiscountCodeData.error = handleError(err);
      return null;
    }
  }).bind(this);

  upgradeOrganizationPlan = flow(function* upgradeOrganizationPlan(
    this: BillingStore,
    variables: {
      orgId: string;
      targetTierId: string;
      additionalSeats: number;
      billingInterval: BillingInterval;
      addons: string[];
    },
  ) {
    try {
      this.upgradeOrganizationPlanData.status = RequestStatus.LOADING;

      const { orgId, targetTierId, additionalSeats, billingInterval, addons } = variables;

      const upgradeOrganizationPlanResponse = yield graphqlApi(UPGRADE_PLAN_MUTATION, {
        orgId,
        input: {
          targetTierId,
          additionalSeats,
          addons,
          billingInterval,
        },
      });
      const data = handleMutationResponse(upgradeOrganizationPlanResponse, 'upgradeOrgPlan');
      this.upgradeOrganizationPlanData.data = data;
      this.upgradeOrganizationPlanData.status = RequestStatus.SUCCESS;
      return data;
    } catch (err) {
      toast('Something went wrong!', { type: 'error' });
      this.upgradeOrganizationPlanData.data = null;
      this.upgradeOrganizationPlanData.status = RequestStatus.FAILED;
      this.upgradeOrganizationPlanData.error = handleError(err);
      return null;
    }
  }).bind(this);

  downgradeOrganizationPlan = flow(function* downgradeOrganizationPlan(
    this: BillingStore,
    variables: {
      orgId: string;
      targetTierId: string;
      billingInterval: BillingInterval;
    },
  ) {
    try {
      this.downgradeOrganizationPlanData.status = RequestStatus.LOADING;
      const { orgId, targetTierId, billingInterval } = variables;
      const downgradeOrganizationPlanResponse = yield graphqlApi(DOWNGRADE_PLAN_MUTATION, {
        orgId,
        input: {
          targetTierId,
          billingInterval,
        },
      });
      const data = handleMutationResponse(downgradeOrganizationPlanResponse, 'downgradeOrgPlan');
      this.downgradeOrganizationPlanData.data = data;
      this.downgradeOrganizationPlanData.status = RequestStatus.SUCCESS;
      return data;
    } catch (err) {
      toast('Something went wrong!', { type: 'error' });
      this.downgradeOrganizationPlanData.data = null;
      this.downgradeOrganizationPlanData.status = RequestStatus.FAILED;
      this.downgradeOrganizationPlanData.error = handleError(err);
      return null;
    }
  }).bind(this);

  cancelDowngradeOrganizationPlan = flow(function* cancelDowngradeOrganizationPlan(
    this: BillingStore,
    orgId: string,
  ) {
    try {
      this.cancelDowngradeOrganizationPlanData.status = RequestStatus.LOADING;
      const cancelDowngradeOrganizationPlanResponse = yield graphqlApi(
        CANCEL_DOWNGRADE_ORG_PLAN_MUTATION,
        { orgId },
      );
      const data = handleMutationResponse(
        cancelDowngradeOrganizationPlanResponse,
        'cancelDowngradeOrgPlan',
      );
      this.cancelDowngradeOrganizationPlanData.data = data;
      this.cancelDowngradeOrganizationPlanData.status = RequestStatus.SUCCESS;
      return data;
    } catch (err) {
      toast('Something went wrong!', { type: 'error' });
      this.cancelDowngradeOrganizationPlanData.data = null;
      this.cancelDowngradeOrganizationPlanData.status = RequestStatus.FAILED;
      this.cancelDowngradeOrganizationPlanData.error = handleError(err);
      return null;
    }
  }).bind(this);

  getOrgCustomPrices = flow(function* getOrgCustomPrices(this: BillingStore, orgId: string) {
    try {
      this.customRatesData.status = RequestStatus.LOADING;
      const getOrgCustomPricesResponse = yield graphqlApi(GET_ORG_CUSTOM_PRICES, { orgId });
      this.customRatesData.data = getOrgCustomPricesResponse?.data?.data?.getOrgCustomPrices;
      this.customRatesData.status = RequestStatus.SUCCESS;
      return this.customRatesData.data;
    } catch (err) {
      toast('Something went wrong!', { type: 'error' });
      this.customRatesData.data = null;
      this.customRatesData.status = RequestStatus.FAILED;
      this.customRatesData.error = handleError(err);
      return [];
    }
  }).bind(this);

  removeCustomPrice = flow(function* removeCustomPrice(this: BillingStore, id: string, orgId: string) {
    try {
      this.removeCustomPriceData.status = RequestStatus.LOADING;
      const response = yield graphqlApi(DELETE_CUSTOM_PRICE_MUTATION, { id, orgId });
      const data = response?.data?.data?.deleteCustomPrice;
      if (data) {
        this.removeCustomPriceData.status = RequestStatus.SUCCESS;
        toast('Custom price restored successfully!', { type: 'success' });
        // Optionally refetch custom prices or billing data here
        return true;
      }
      toast('Failed to restore custom price', { type: 'error' });
      this.removeCustomPriceData.status = RequestStatus.FAILED;
      return false;
    } catch (err) {
      toast('Failed to restore custom price', { type: 'error' });
      this.removeCustomPriceData.status = RequestStatus.FAILED;
      this.removeCustomPriceData.error = handleError(err);
      return false;
    }
  }).bind(this);

  saveCustomPrices = flow(function* saveCustomPrices(
    this: BillingStore,
    orgId: string,
    prices: SaveCustomPriceInput[],
  ) {
    try {
      this.saveCustomPricesData.status = RequestStatus.LOADING;
      const saveCustomPricesResponse = yield graphqlApi(SAVE_CUSTOM_PRICES_MUTATION, {
        orgId,
        inputs: prices,
      });
      const data = handleMutationResponse(saveCustomPricesResponse, 'saveCustomPrices');
      this.saveCustomPricesData.data = data;
      const allSuccess = data?.every((price: SaveCustomPriceResult) => price.success);

      if (!allSuccess) {
        const errorMessages =
          data
            ?.filter((price: SaveCustomPriceResult) => !price.success)
            .map((price: SaveCustomPriceResult) => price.message)
            .join('\n') || null;
        throw new Error(errorMessages);
      }
      this.saveCustomPricesData.status = RequestStatus.SUCCESS;
      return data;
    } catch (err) {
      toast((handleError(err) as string) || 'Something went wrong!', { type: 'error' });
      this.saveCustomPricesData.data = [];
      this.saveCustomPricesData.status = RequestStatus.FAILED;
      this.saveCustomPricesData.error = handleError(err);
      return null;
    }
  }).bind(this);

  saveOrganizationBillingDetails = flow(function* saveOrganizationBillingDetails(
    this: BillingStore,
    orgId: string,
    input: UpdateOrganizationBillingInput,
  ) {
    try {
      this.saveOrganizationBillingDetailsData.status = RequestStatus.LOADING;

      const saveOrganizationBillingDetailsResponse = yield graphqlApi(
        SAVE_ORGANIZATION_BILLING_DETAILS_MUTATION,
        {
          orgId,
          input,
        },
      );

      const data = handleMutationResponse(
        saveOrganizationBillingDetailsResponse,
        'saveOrganizationBillingDetails',
      );

      this.saveOrganizationBillingDetailsData.data = data;
      this.saveOrganizationBillingDetailsData.status = RequestStatus.SUCCESS;

      if (data?.success) {
        return data;
      }
      toast(data?.message || 'Something went wrong!', { type: 'error' });
      this.saveOrganizationBillingDetailsData.status = RequestStatus.FAILED;
      return null;
    } catch (err) {
      toast('Failed to save billing details', { type: 'error' });
      this.saveOrganizationBillingDetailsData.data = null;
      this.saveOrganizationBillingDetailsData.status = RequestStatus.FAILED;
      this.saveOrganizationBillingDetailsData.error = handleError(err);

      return null;
    }
  }).bind(this);

  createStripeCustomer = flow(function* createStripeCustomer(this: BillingStore, orgId: string) {
    try {
      this.createStripeCustomerData.status = RequestStatus.LOADING;

      const response = yield graphqlApi(CREATE_STRIPE_CUSTOMER_MUTATION, { orgId });

      const data = handleMutationResponse(response, 'createStripeCustomer');

      this.createStripeCustomerData.data = data;
      this.createStripeCustomerData.status = RequestStatus.SUCCESS;

      if (data?.created && data?.customerId) {
        toast('Stripe customer created successfully!', { type: 'success' });
      }

      return data;
    } catch (err) {
      toast('Failed to create Stripe customer', { type: 'error' });
      this.createStripeCustomerData.data = null;
      this.createStripeCustomerData.status = RequestStatus.FAILED;
      this.createStripeCustomerData.error = handleError(err);
      return null;
    }
  }).bind(this);

  get invoices(): Invoice[] {
    return this.invoicesData.data || [];
  }

  get organizationBilling(): OrganizationBilling | null {
    return this.organizationBillingData.data || null;
  }

  get discountCodes(): DiscountCode[] {
    return this.discountCodesData.data || [];
  }
}
